import type { Transaction } from "@/apis/gql/types";
import { useTable } from "@/components/shared/data-table/useTable";
import { useServerSideQuery } from "@/hooks/useQueryFilters";
import { useTablePagination } from "@/hooks/useTablePagination";
import { useGetAllTransactions } from "@/queries/transactions/get-all";
import type { ColumnDef, Row } from "@tanstack/react-table";

const storageKey = "all-transactions";

type TransactionFilterFields = "date" | "amount" | "status" | "type";

export type AllTransactionsTableInstance = ReturnType<
  typeof useAllTransactionsTable
>;

export function useAllTransactionsTable({
  columns,
  rowSelection,
  onRowSelectionChange,
}: {
  columns: ColumnDef<Transaction>[];
  rowSelection?: Record<string, boolean>;
  onRowSelectionChange?: (value: Record<string, boolean>) => void;
}) {
  const { page, setPage, limit, setLimit } = useTablePagination();

  const {
    globalSearch,
    setGlobalSearch,
    serverSideFilters,
    setFilters,
    filters,
    sorting,
    onSortingChange,
    sort,
    order,
    initialServerSideFilters,
    resetFilters,
  } = useServerSideQuery<TransactionFilterFields>({
    storageKey,
    defaultFilters: {},
    defaultSorting: { sort: "bookingDate", order: "desc" },
  });

  const { data, loading, error, pagination } = useGetAllTransactions({
    query: serverSideFilters,
    global: globalSearch,
    page,
    limit,
    offset: (page - 1) * limit,
    sort,
    order,
  });

  const table = useTable({
    data: data ?? [],
    columns,
    tableName: "transactions",
    getRowId: (row) => row.bankTxnId,
    focusedRowUrlParamName: "transaction_id",
    sorting,
    onSortingChange,
    manualFiltering: true,
    manualSorting: true,
    manualPagination: true,
    rowSelection,
    onRowSelectionChange,
    initialServerSideFilters,
    onServerSideFiltersClear: resetFilters,
    serverSideFilters,
    globalServerSideSearch: globalSearch,
  });

  const handleRowClick = (
    event: React.MouseEvent,
    row: Row<Transaction>,
    index: number,
  ) => {
    table.setRowSelection((prev: Record<string, boolean>) => {
      const isSelected = !!prev[row.id];
      const selectedCount = Object.values(prev).filter(Boolean).length;

      return { [row.id]: !isSelected || selectedCount !== 1 };
    });
  };

  const handlePageChange = (page: number) => {
    setPage(page);
  };

  const handlePageSizeChange = (pageSize: number) => {
    setLimit(pageSize);
  };

  return {
    ...table,
    loading,
    error,
    pagination,
    handleRowClick,
    handlePageChange,
    handlePageSizeChange,
    globalSearch,
    setGlobalSearch,
    filters,
    setFilters,
    limit,
  };
}
